import { useEffect, useMemo, useState } from 'react';

import { ColorSchemePreferenceEnum } from '../../enums/ColorSchemePreferenceEnum';
import { useAppStore } from '../../store/store';

export type ResolvedColorScheme = 'light' | 'dark';

const prefersDarkMediaQuery = '(prefers-color-scheme: dark)';

function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia(prefersDarkMediaQuery).matches;
}

export function resolveColorScheme(
  preference: ColorSchemePreferenceEnum,
  systemPrefersDark: boolean,
): ResolvedColorScheme {
  if (preference === ColorSchemePreferenceEnum.Dark) {
    return 'dark';
  }

  if (preference === ColorSchemePreferenceEnum.Light) {
    return 'light';
  }

  return systemPrefersDark ? 'dark' : 'light';
}

export function useColorScheme() {
  const colorSchemePreference = useAppStore((state) => state.colorSchemePreference);
  const setColorSchemePreference = useAppStore((state) => state.setColorSchemePreference);
  const reduceMotionEnabled = useAppStore((state) => state.reduceMotionEnabled);
  const highContrastEnabled = useAppStore((state) => state.highContrastEnabled);
  const [systemPrefersDark, setSystemPrefersDark] = useState(readSystemPrefersDark);
  const [systemPrefersReducedMotion, setSystemPrefersReducedMotion] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    if (colorSchemePreference !== ColorSchemePreferenceEnum.System) {
      return;
    }

    const mediaQueryList = window.matchMedia(prefersDarkMediaQuery);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };

    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [colorSchemePreference]);

  const resolvedColorScheme = useMemo(
    () => resolveColorScheme(colorSchemePreference, systemPrefersDark),
    [colorSchemePreference, systemPrefersDark],
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedColorScheme === 'dark');
  }, [resolvedColorScheme]);

  useEffect(() => {
    document.documentElement.classList.toggle('high-contrast', highContrastEnabled);
  }, [highContrastEnabled]);

  useEffect(() => {
    const mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncReducedMotion = (event?: MediaQueryListEvent) => {
      const prefersReducedMotion = event?.matches ?? mediaQueryList.matches;
      setSystemPrefersReducedMotion(prefersReducedMotion);
    };

    syncReducedMotion();
    mediaQueryList.addEventListener('change', syncReducedMotion);
    return () => mediaQueryList.removeEventListener('change', syncReducedMotion);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle(
      'reduce-motion',
      reduceMotionEnabled || systemPrefersReducedMotion,
    );
  }, [reduceMotionEnabled, systemPrefersReducedMotion]);

  return {
    colorSchemePreference,
    resolvedColorScheme,
    setColorSchemePreference,
    highContrastEnabled,
    reduceMotionEnabled,
  };
}
